import React, { useState } from "react";
import { Button, ButtonSet, Form } from "carbon-components-react";
import { Formik } from "formik";

import { AppShape } from "@eave/shared/types/api";
import { thereWasAProblem } from "@eave/shared/utils/alerts";

import APIClient from "web_modules/APIClient";
import DollarInput from "web_modules/shared/carbon/DollarInput";
import {
  expectedGrossMonthlyRentalIncomeValidationSchema,
  initialExpectedGrossMonthlyRentalIncomeFromApp,
} from "./form-values";
import {
  ExpectedGrossMonthlyRentalIncomePayload,
  ExpectedGrossMonthlyRentalIncomeFormValues,
} from "./shared";

const styles = require("../form.module.scss");

interface Props {
  app: AppShape;
  reloadApp: () => void;
}

const getPayload = ({
  expected_gross_monthly_rental_income,
}: ExpectedGrossMonthlyRentalIncomeFormValues): ExpectedGrossMonthlyRentalIncomePayload => {
  if (expected_gross_monthly_rental_income === "") {
    return { expected_gross_monthly_rental_income: null };
  }

  return { expected_gross_monthly_rental_income };
};

const ExpectedGrossMonthlyRentalIncome = ({ app, reloadApp }: Props) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const updateLoanApp = (
    appId: number,
    values: ExpectedGrossMonthlyRentalIncomePayload,
  ): Promise<void> => APIClient.put(`/loan_applications/${appId}`, values);

  const onSubmit = async (values: ExpectedGrossMonthlyRentalIncomeFormValues) => {
    setSaving(true);

    try {
      await updateLoanApp(app.id, getPayload(values));
      reloadApp();
    } catch (e) {
      thereWasAProblem("saving expected gross monthly rental income changes");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>Subject property gross monthly rental income</div>
      <Formik
        initialValues={initialExpectedGrossMonthlyRentalIncomeFromApp(app)}
        validationSchema={expectedGrossMonthlyRentalIncomeValidationSchema}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ dirty, errors, handleBlur, handleChange, handleSubmit, resetForm, values }) => {
          const hasErrors = Object.keys(errors).length > 0;

          return (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
              className={styles.form}
            >
              <div className={styles.buttons}>
                {!editing ? (
                  <Button kind="secondary" size="sm" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                ) : (
                  <ButtonSet>
                    <Button
                      kind="tertiary"
                      size="sm"
                      onClick={() => {
                        setEditing(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button disabled={!dirty || hasErrors} kind="primary" size="sm" type="submit">
                      Save
                    </Button>
                  </ButtonSet>
                )}
              </div>
              <div className={styles.inputs}>
                <DollarInput
                  id="expected-gross-monthly-rental-income"
                  name="expected_gross_monthly_rental_income"
                  value={values.expected_gross_monthly_rental_income}
                  labelText="Subject property gross monthly rental income"
                  invalid={!!errors.expected_gross_monthly_rental_income}
                  invalidText={errors.expected_gross_monthly_rental_income}
                  disabled={saving || !editing}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ExpectedGrossMonthlyRentalIncome;
